import {
  Box,
  Typography,
  Chip,
  IconButton,
  LinearProgress,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircleOutline,
  DownloadOutlined,
  PrintOutlined,
  ErrorOutline,
} from "@mui/icons-material";
import { tokens } from "../../../../theme";
import type { IBrandModel, IEquipment } from "../../../../types/equipment";
import Button from "../../../../components/ui/Button";
import { useEffect, useState, useCallback } from "react";
import apiService from "../../../../service/Api";
import type { EquipmentRental } from "./RentalsTab";
import ReturnGearDialog, { ReturnFormData } from "../ReturnGear";

interface Props {
  equipment: IEquipment;
  brandModel?: IBrandModel | null;
  onEquipmentUpdated?: () => void;
}

interface DashboardData {
  purchase_cost?: number | string | null;
  total_income?: number | string | null;
  maintenance_cost?: number | string | null;
  net_profit?: number | string | null;
  roi?: number | string | null;
  cost_recovery?: number | string | null;
  utilization?: number | string | null;
  health_score?: number | string | null;

  active_rental?: EquipmentRental | null;

  current_allocation?: {
    allocated_to_name?: string;
    allocated_to_lastname?: string;
    stream?: { title?: string; name?: string };
    gearset?: { name?: string };
    allocation_date?: string;
  } | null;
}
export default function OverviewTab({ equipment, brandModel, onEquipmentUpdated }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [activeRental, setActiveRental] = useState<EquipmentRental | null>(
    null,
  );

  const fetchDashboard = useCallback(async () => {
    try {
      setDashLoading(true);
      const res = await apiService.get<DashboardData>(
        `/api/equipments/equipment/${equipment.id}/equipment-dashboard/`,
      );
      setDashboard(res.data);
    } catch (err) {
      console.error("Failed to fetch dashboard", err);
      setDashboard(null);
    } finally {
      setDashLoading(false);
    }
  }, [equipment.id]);

  const fetchActiveRental = useCallback(async () => {
    try {
      const res = await apiService.get<{
        results: EquipmentRental[];
      }>(
        `/api/equipments/equipment-rentals/?equipment=${equipment.id}&page=1&page_size=50`,
      );

      const active =
        res.data.results.find(
          (r) => r.status === "active" || r.status === "overdue",
        ) || null;

      setActiveRental(active);
    } catch (err) {
      console.error("Failed to fetch active rental", err);
      setActiveRental(null);
    }
  }, [equipment.id]);

  useEffect(() => {
    fetchDashboard();
    fetchActiveRental();
  }, [fetchDashboard, fetchActiveRental]);

  const handleReturnSubmit = async (data: ReturnFormData) => {
    const rental = dashboard?.active_rental;
    if (!rental) return;

    const payload: Record<string, unknown> = {
      status: "returned",
      condition_after: data.conditionAfter,
      notes: data.notes || null,
      overdue_return_date: data.isOverdue ? data.returnDate : null,
    };

    await apiService.patch(
      `/api/equipments/equipment-rentals/${rental.id}/`,
      payload,
    );

    setReturnDialogOpen(false);
    await fetchDashboard();
    onEquipmentUpdated?.();
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatMoney = (value?: number | string | null) => {
    if (value === null || value === undefined || value === "") return "N/A";
    return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
  };

  const clientName = activeRental
    ? [activeRental.contact_name, activeRental.contact_last_name]
        .filter(Boolean)
        .join(" ") || String(activeRental.client_name || "—")
    : null;

  const isOverdue = activeRental?.status === "overdue";
  const costRecovery = Number(dashboard?.cost_recovery ?? 0);
  const healthScore = Number(dashboard?.health_score ?? 0);
  const utilization = Number(dashboard?.utilization ?? 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* TOP ROW */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          gap: 3,
        }}
      >
        {/* Finance Card */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={3}>
            Finance
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: 2,
              mb: 4,
            }}
          >
            {[
              {
                label: "Purchase cost",
                value: equipment.purchase_cost
                  ? `$${equipment.purchase_cost}`
                  : "N/A",
                bg: colors.neutral[200],
              },
              {
                label: "Rental Income",
                value: dashLoading
                  ? "..."
                  : formatMoney(dashboard?.total_income),
                bg: colors.secondary[500],
              },
              {
                label: "ROI",
                value: dashLoading
                  ? "..."
                  : dashboard?.roi !== null && dashboard?.roi !== undefined
                    ? `${Number(dashboard.roi).toFixed(1)}%`
                    : "N/A",
                bg: colors.primary[600],
              },
              {
                label: "Maint. Cost",
                value: dashLoading
                  ? "..."
                  : formatMoney(dashboard?.maintenance_cost),
                bg: colors.primary[800],
              },
            ].map((stat, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: stat.bg,
                  borderRadius: "8px",
                  p: 2,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color={colors.primary[700]}
                >
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Cost Recovery
            </Typography>
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              {dashLoading
                ? "..."
                : `Recovered ${Number(costRecovery).toFixed(0)}% of cost via rentals`}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(Number(costRecovery), 100)}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: colors.primary[800],
              "& .MuiLinearProgress-bar": {
                backgroundColor: colors.secondary[10],
                borderRadius: 5,
              },
            }}
          />
        </Box>

        {/* Rental Snapshot Card */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Rental Snapshot
          </Typography>

          {dashLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activeRental ? (
            <>
              <Box
                sx={{
                  border: `1px solid ${isOverdue ? "#f8bbd0" : theme.palette.divider}`,
                  bgcolor: colors.primary[800],
                  borderRadius: "10px",
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.primary"
                    >
                      {clientName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Return: {formatDate(activeRental.rental_end_date)}
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      activeRental.status.charAt(0).toUpperCase() +
                      activeRental.status.slice(1)
                    }
                    size="small"
                    sx={{
                      bgcolor: isOverdue ? "#ffebee" : "#E1F5EE",
                      color: isOverdue ? "#EF4444" : "#1D9E75",
                      fontWeight: 600,
                      height: 24,
                      textTransform: "capitalize",
                    }}
                  />
                </Box>
                {isOverdue && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <ErrorOutline sx={{ color: "#ed6c02", fontSize: 18 }} />
                    <Typography variant="caption" color="text.secondary">
                      Overdue — action required
                    </Typography>
                  </Box>
                )}
                {!isOverdue && <Box mb={2} />}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CheckCircleOutline />}
                  onClick={() => setReturnDialogOpen(true)}
                  sx={{
                    bgcolor: colors.primary[400],
                    color: colors.primary[900],
                    textTransform: "none",
                    py: 1.5,
                    borderRadius: "8px",
                    "&:hover": { bgcolor: colors.primary[400] },
                  }}
                >
                  Mark as returned
                </Button>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  color: colors.primary[400],
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                View Full Rental History
              </Typography>
            </>
          ) : (
            <Box
              sx={{
                bgcolor: colors.primary[800],
                borderRadius: "10px",
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No active rental
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* MIDDLE ROW */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
        }}
      >
        {/* Physical Specs */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={3}>
            Physical Specs
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {[
              {
                label: "Length",
                value: brandModel?.length ? `${brandModel?.length} cm` : "N/A",
              },
              {
                label: "Height",
                value: brandModel?.height ? `${brandModel?.height} cm` : "N/A",
              },
              {
                label: "Weight",
                value: brandModel?.weight ? `${brandModel?.weight} kg` : "N/A",
              },
              {
                label: "Transport Volume",
                value: equipment.transport_volume ? `${equipment.transport_volume} cm³` : "N/A",
              },
              {
                label: "Current",
                value: brandModel?.current_draw
                  ? `${brandModel?.current_draw} A`
                  : "N/A",
              },
              {
                label: "Power",
                value: brandModel?.power_draw ? `${brandModel?.power_draw} W` : "N/A",
              },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.primary"
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Identification */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={3}>
            Identification
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                bgcolor: colors.primary[800],
                borderRadius: "8px",
                display: "grid",
                placeItems: "center",
                border: `1px solid ${colors.primary[800]}`,
              }}
            >
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${equipment.serial_number}`}
                alt="QR"
                style={{ width: "90%", height: "90%" }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                sx={{
                  border: `1px solid ${colors.primary[400]}`,
                  borderRadius: "8px",
                  color: colors.primary[400],
                }}
              >
                <DownloadOutlined />
              </IconButton>
              <IconButton
                sx={{
                  border: `1px solid ${colors.primary[400]}`,
                  borderRadius: "8px",
                  color: "text.secondary",
                }}
              >
                <PrintOutlined sx={{ color: colors.primary[400] }} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Serial Number
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {equipment.serial_number}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body2" fontWeight={500} color="text.primary">
                {equipment.location || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Supplier */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={3}>
            Supplier
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {[
              { label: "Supplier", value: equipment.supplier_name || "N/A" },
              { label: "Contact", value: equipment.contact_name || "N/A" },
              { label: "Phone", value: equipment.supplier_phone || "N/A" },
              { label: "Email", value: equipment.supplier_email || "N/A" },
              {
                label: "Cost",
                value: equipment.purchase_cost
                  ? `$${equipment.purchase_cost}`
                  : "N/A",
              },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.primary"
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* BOTTOM ROW */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
        }}
      >
        {/* Utilization */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Utilization
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2 }}>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {dashLoading ? "..." : utilization}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              this month
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min((utilization / 30) * 100, 100)}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: colors.primary[800],
              "& .MuiLinearProgress-bar": {
                backgroundColor: colors.neutral[300],
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {/* Health Score */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Health Score
          </Typography>
          <Box
            sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 1 }}
          >
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {dashLoading ? "..." : healthScore}
            </Typography>
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              /100
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Based on maintenance frequency and condition
          </Typography>
        </Box>

        {/* Current Allocation */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Current Allocation
          </Typography>
          {dashLoading ? (
            <CircularProgress size={20} />
          ) : dashboard?.current_allocation ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[
                {
                  label: "Allocated To",
                  value:
                    [
                      dashboard.current_allocation.allocated_to_name,
                      dashboard.current_allocation.allocated_to_lastname,
                    ]
                      .filter(Boolean)
                      .join(" ") || "—",
                },
                {
                  label: "Stream",
                  value: dashboard.current_allocation.stream?.name || "—",
                },
                {
                  label: "Gear Set",
                  value: dashboard.current_allocation.gearset?.name || "—",
                },
                {
                  label: "Date",
                  value: formatDate(
                    dashboard.current_allocation.allocation_date,
                  ),
                },
              ].map((item, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="text.primary"
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not currently allocated
            </Typography>
          )}
        </Box>
      </Box>

      {/* Return Dialog */}
      <ReturnGearDialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        onSubmit={handleReturnSubmit}
        rental={activeRental ?? null}
      />
    </Box>
  );
}
